import OwnershipStep from '../components/OwnershipStep.vue'
import ValuationStep from '../components/ValuationStep.vue'

import { WizardStepDef } from './types'
import {
  OWNERSHIP_STEP_KEY,
  SUMMARY_STEP_KEY,
  TAGS_STEP_KEY,
  VALUE_STEP_KEY,
} from './const'
import { ownerAccountFields } from './fields'

/**
 * The steps every wizard shares.
 *
 * Tags and Summary are real; the rest are placeholders for work in progress.
 * A placeholder is not an empty panel — it carries the assistant asking for
 * exactly what the step will collect, so the gap is visible and the user has
 * somewhere to put the answer when the step lands.
 */

/**
 * Where the records will hang: an account the repository already has, or one
 * created alongside them. Its own component because the step is a choice
 * between two shapes rather than a grid of fields — but it still declares its
 * fields, so the rail can count what is outstanding either way.
 */
export const OWNERSHIP_STEP: WizardStepDef = {
  key: OWNERSHIP_STEP_KEY,
  label: 'Ownership & account',
  heading: 'Where does this sit?',
  description:
    'Every record hangs off an account. Pick the one it belongs to, or create it here.',
  component: OwnershipStep,
  fields: ownerAccountFields(),
}

export const TAGS_STEP: WizardStepDef = {
  key: TAGS_STEP_KEY,
  label: 'Tags',
  badge: 'Optional',
}

export const SUMMARY_STEP: WizardStepDef = {
  key: SUMMARY_STEP_KEY,
  label: 'Summary',
}

/**
 * What the asset is worth, as dated marks — the step spec 3a draws.
 *
 * One step for every flow that holds a standing asset. Only the words change:
 * a fund is asked for its NAV and a property for what it is worth, and both
 * record the same thing — a value, a date and which of the two editable
 * sources it is.
 *
 * Requires the flow's schema to declare `prices` and its create to write them —
 * see `markList` and `createMarks`. Optional in the only sense that matters: it
 * declares no fields, so the rail counts nothing here and nothing on it blocks
 * a create.
 */
export const valueStep = (overrides: Partial<WizardStepDef> = {}): WizardStepDef => ({
  key: VALUE_STEP_KEY,
  label: 'Valuation',
  heading: 'What is it worth?',
  badge: 'Optional',
  description:
    'Record what you paid, an appraisal, or a later mark. Each one dates the value it sets.',
  component: ValuationStep,
  ...overrides,
})

/** The step as most flows ask it. A fund words it differently — see PrivateFund. */
export const VALUE_STEP: WizardStepDef = valueStep()

